import React, { useEffect, useMemo, useState } from "react";
import { api, API_BASE, getToken } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Landmark, Plus, MessageSquare, Trash2, Send, CheckCircle2, XCircle, Clock3, Archive, Pencil, Paperclip, X, Printer, Download, Eye, Lock, Users } from "lucide-react";
import { toast } from "sonner";
import { printCapitalReport } from "@/lib/printCapital";

const CUR_SYM = { USD: "US$", CAD: "CA$", GBP: "£", EUR: "€" };
const CURRENCIES = ["USD", "CAD", "GBP", "EUR"];
const CATEGORIES = ["Aircraft", "Facilities", "Operations", "Tooling", "IT", "Other"];
const PRIORITIES = ["Elective", "Necessary", "Mandatory"];
const PAGE_SIZE = 20;

const money = (v, cur) => (v === null || v === undefined || v === "" ? "—" : `${CUR_SYM[cur] || "$"}${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2 })}`);
const fmtWhen = (iso) => (iso ? new Date(iso).toLocaleString("en-CA", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "");
const byActivity = (a, b) => String(b.last_activity_at || b.created_at || "").localeCompare(String(a.last_activity_at || a.created_at || ""));

// Fiscal year runs September → August.
const fyOf = (iso) => { const d = new Date(iso); return d.getMonth() >= 8 ? d.getFullYear() : d.getFullYear() - 1; };
const fyLabel = (y) => `Sep ${y} – Aug ${y + 1}`;
const CURRENT_FY = (() => { const n = new Date(); return n.getMonth() >= 8 ? n.getFullYear() : n.getFullYear() - 1; })();

const PRIORITY_CLS = {
  Mandatory: "bg-rose-100 text-rose-800",
  Necessary: "bg-amber-100 text-amber-800",
  Elective: "bg-sky-100 text-sky-800",
};
const STATUS_CLS = {
  pending: "bg-amber-100 text-amber-800",
  approved: "bg-emerald-100 text-emerald-800",
  denied: "bg-rose-100 text-rose-800",
};

export function CapitalStatusBadge({ status }) {
  return (
    <span className={`px-2 py-0.5 rounded-full font-mono text-[10px] uppercase tracking-widest ${STATUS_CLS[status] || "bg-gray-100 text-gray-600"}`}>
      {status}
    </span>
  );
}

function RaiseDialog({ open, onOpenChange, approvers, onSaved, initial }) {
  const empty = { title: "", description: "", category: "Other", priority: "Necessary", fleet: "", estimated_cost: "", currency: "CAD", approver_id: "" };
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open) return;
    setForm(initial ? {
      title: initial.title || "",
      description: initial.description || "",
      category: initial.category || "Other",
      priority: initial.priority || "Necessary",
      fleet: initial.fleet || "",
      estimated_cost: initial.estimated_cost ?? "",
      currency: initial.currency || "CAD",
      approver_id: initial.approver_id || "",
    } : empty);
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.title.trim()) { toast.error("Title is required"); return; }
    if (!form.approver_id) { toast.error("Pick an approver"); return; }
    setBusy(true);
    try {
      const body = { ...form, estimated_cost: form.estimated_cost === "" ? null : Number(form.estimated_cost) };
      const r = initial ? await api.patch(`/capital/${initial.id}`, body) : await api.post("/capital", body);
      onSaved(r.data);
      onOpenChange(false);
      toast.success(initial ? "Request updated" : "Capital request raised — members notified");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save request");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="capital-form-dialog">
        <DialogHeader><DialogTitle className="font-display">{initial ? "Edit capital request" : "Raise capital request"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs uppercase tracking-widest text-gray-500">Title *</Label>
            <Input className="mt-1" value={form.title} onChange={(e) => set("title", e.target.value)} placeholder="e.g. Replacement engine hoist" data-testid="capital-form-title" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-gray-500">Description / justification</Label>
            <Textarea className="mt-1" rows={3} value={form.description} onChange={(e) => set("description", e.target.value)} placeholder="Why is this needed, impact if deferred…" data-testid="capital-form-description" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-widest text-gray-500">Category</Label>
              <Select value={form.category} onValueChange={(v) => set("category", v)}>
                <SelectTrigger className="mt-1" data-testid="capital-form-category"><SelectValue /></SelectTrigger>
                <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c} data-testid={`capital-category-${c.toLowerCase()}`}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-gray-500">Priority</Label>
              <Select value={form.priority} onValueChange={(v) => set("priority", v)}>
                <SelectTrigger className="mt-1" data-testid="capital-form-priority"><SelectValue /></SelectTrigger>
                <SelectContent>{PRIORITIES.map((p) => <SelectItem key={p} value={p} data-testid={`capital-priority-${p.toLowerCase()}`}>{p}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-widest text-gray-500">Fleet / aircraft (optional)</Label>
              <Input className="mt-1" value={form.fleet} onChange={(e) => set("fleet", e.target.value)} placeholder="e.g. D228" data-testid="capital-form-fleet" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-gray-500">Estimated cost</Label>
              <div className="flex gap-2 mt-1">
                <select className="h-9 px-1.5 rounded-md border border-summit-hairline bg-white text-xs" value={form.currency} onChange={(e) => set("currency", e.target.value)} data-testid="capital-form-currency">
                  {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
                <Input type="number" min="0" step="0.01" value={form.estimated_cost} onChange={(e) => set("estimated_cost", e.target.value)} data-testid="capital-form-cost" />
              </div>
            </div>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-gray-500">Approver *</Label>
            <Select value={form.approver_id} onValueChange={(v) => set("approver_id", v)}>
              <SelectTrigger className="mt-1" data-testid="capital-approver-select"><SelectValue placeholder="Pick a Capital member" /></SelectTrigger>
              <SelectContent>
                {approvers.map((a) => (
                  <SelectItem key={a.id} value={a.id} data-testid={`capital-approver-option-${a.id}`}>
                    {a.name}{a.job_title ? ` — ${a.job_title}` : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button className="w-full bg-summit-primary hover:bg-summit-primary-soft text-white" onClick={submit} disabled={busy} data-testid="capital-submit-btn">
            <Send className="w-4 h-4 mr-1.5" /> {initial ? "Save changes" : "Raise request"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function AttachmentsSection({ item, user, busy, setBusy, onUpdated }) {
  const upload = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    const fd = new FormData();
    fd.append("file", f);
    setBusy(true);
    try {
      const r = await api.post(`/capital/${item.id}/attachments`, fd);
      onUpdated(r.data);
      toast.success(`Attached ${f.name}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
    }
  };

  const download = async (a) => {
    try {
      const resp = await fetch(`${API_BASE}/capital/${item.id}/attachments/${a.id}`, { headers: { Authorization: `Bearer ${getToken()}` } });
      if (!resp.ok) throw new Error();
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const el = document.createElement("a");
      el.href = url;
      el.download = a.filename || "attachment";
      el.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Download failed");
    }
  };

  const remove = async (a) => {
    try {
      const r = await api.delete(`/capital/${item.id}/attachments/${a.id}`);
      onUpdated(r.data);
      toast.success("Attachment removed");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to remove");
    }
  };

  const list = item.attachments || [];
  return (
    <div>
      <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500 mb-2">Attachments ({list.length})</div>
      <div className="space-y-1.5" data-testid="capital-attachments-list">
        {list.map((a) => (
          <div key={a.id} className="flex items-center gap-2 text-xs border border-summit-hairline rounded-md px-2.5 py-1.5 bg-white" data-testid={`capital-attachment-${a.id}`}>
            <Paperclip className="w-3.5 h-3.5 text-gray-400 shrink-0" />
            <button className="truncate font-medium hover:underline" onClick={() => download(a)} data-testid={`capital-attach-dl-${a.id}`}>{a.filename}</button>
            <span className="text-gray-400 shrink-0">{Math.max(1, Math.round((a.size || 0) / 1024))} KB · {a.uploaded_by_name}</span>
            {(user.role === "admin" || a.uploaded_by === user.id || item.created_by === user.id) && (
              <button className="ml-auto p-0.5 rounded text-gray-400 hover:text-rose-600 shrink-0" onClick={() => remove(a)} data-testid={`capital-attach-del-${a.id}`}>
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        ))}
      </div>
      <label className={`mt-2 inline-flex items-center gap-1.5 text-xs text-summit-primary hover:underline cursor-pointer ${busy ? "opacity-50 pointer-events-none" : ""}`}>
        <Paperclip className="w-3.5 h-3.5" /> Attach document
        <input type="file" className="hidden" onChange={upload} data-testid="capital-attach-input" />
      </label>
    </div>
  );
}

function ChainStrip({ item }) {
  const levels = (item.approvals || []).map((a) => ({ n: a.level, state: "done" }));
  if (item.status === "pending") levels.push({ n: item.approval_level || 1, state: "current" });
  else if (item.status === "denied") levels.push({ n: item.approval_level || 1, state: "denied" });
  if (levels.length === 0) return null;
  const cls = {
    done: "bg-emerald-600 text-white",
    current: "bg-amber-400 text-amber-950",
    denied: "bg-rose-600 text-white",
  };
  return (
    <span className="inline-flex items-center" data-testid={`capital-chain-${item.id}`}>
      {levels.map((l, i) => (
        <span key={`${l.n}-${i}`} className="inline-flex items-center">
          {i > 0 && <span className="w-2 h-px bg-gray-300 mx-0.5" />}
          <span className={`px-1 py-px rounded font-mono text-[9px] uppercase tracking-wide ${cls[l.state]}`} data-testid={`capital-chain-${item.id}-l${l.n}-${l.state}`}>
            L{l.n}
          </span>
        </span>
      ))}
    </span>
  );
}

function DetailDialog({ item, onOpenChange, user, onUpdated, onDeleted, onEdit, approvers }) {
  const [comment, setComment] = useState("");
  const [decisionComment, setDecisionComment] = useState("");
  const [forwardTo, setForwardTo] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!item) return;
    api.post(`/capital/${item.id}/seen`).then((r) => onUpdated(r.data)).catch(() => {});
  }, [item?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!item) return null;
  const isApprover = item.approver_id === user.id;
  const isAnyApprover = isApprover || (item.approvals || []).some((a) => a.user_id === user.id) || user.role === "admin";
  const level = item.approval_level || 1;
  const forwardOptions = (approvers || []).filter(
    (a) => a.id !== item.created_by && a.id !== user.id && !(item.approvals || []).some((x) => x.user_id === a.id)
  );
  const canDelete = user.role === "admin" || item.created_by === user.id || !!item.archived;
  const canArchive = !item.archived && item.status !== "pending";
  const canEdit = item.status === "pending" && (item.created_by === user.id || user.role === "admin");

  const decide = async (decision) => {
    setBusy(true);
    try {
      const fwd = decision === "approved" && forwardTo ? forwardTo : null;
      const r = await api.post(`/capital/${item.id}/decision`, { decision, comment: decisionComment, forward_to: fwd });
      onUpdated(r.data);
      setForwardTo("");
      toast.success(fwd ? "Approved — forwarded to the next approver" : `Request ${decision}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to decide");
    } finally {
      setBusy(false);
    }
  };

  const setVisibility = async (v) => {
    try {
      const r = await api.post(`/capital/${item.id}/visibility`, { visibility: v });
      onUpdated(r.data);
      toast.success(v === "members" ? "Now visible to all Capital members" : "Restricted to requestor & approvers");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to change visibility");
    }
  };

  const sendComment = async () => {
    if (!comment.trim()) return;
    setBusy(true);
    try {
      const r = await api.post(`/capital/${item.id}/comments`, { text: comment });
      onUpdated(r.data);
      setComment("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to comment");
    } finally {
      setBusy(false);
    }
  };

  const archive = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/capital/${item.id}/archive`);
      onUpdated(r.data);
      toast.success("Archived");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to archive");
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!window.confirm(`Delete "${item.title}"?`)) return;
    try {
      await api.delete(`/capital/${item.id}`);
      onDeleted(item.id);
      onOpenChange(false);
      toast.success("Deleted");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to delete");
    }
  };

  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl max-h-[85vh] overflow-y-auto" data-testid="capital-detail-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2 flex-wrap">
            {item.title}
            <CapitalStatusBadge status={item.status} />
            <span className={`px-2 py-0.5 rounded-full font-mono text-[10px] uppercase tracking-widest ${PRIORITY_CLS[item.priority] || "bg-gray-100 text-gray-600"}`}>{item.priority}</span>
            {item.archived && <span className="px-2 py-0.5 rounded-full font-mono text-[10px] uppercase tracking-widest bg-gray-200 text-gray-600">archived</span>}
            {(item.visibility || "private") === "private" && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full font-mono text-[10px] uppercase tracking-widest bg-gray-100 text-gray-600" data-testid="capital-private-badge">
                <Lock className="w-3 h-3" /> private
              </span>
            )}
          </DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-sm">
          <div><span className="text-gray-400 text-xs uppercase tracking-widest font-mono">Category</span><div>{item.category}</div></div>
          <div><span className="text-gray-400 text-xs uppercase tracking-widest font-mono">Est. cost</span><div className="font-mono">{money(item.estimated_cost, item.currency)}</div></div>
          <div><span className="text-gray-400 text-xs uppercase tracking-widest font-mono">Raised by</span><div>{item.created_by_name}</div></div>
          <div><span className="text-gray-400 text-xs uppercase tracking-widest font-mono">Approver</span><div>{item.approver_name}</div></div>
          {item.fleet && <div><span className="text-gray-400 text-xs uppercase tracking-widest font-mono">Fleet</span><div>{item.fleet}</div></div>}
          <div><span className="text-gray-400 text-xs uppercase tracking-widest font-mono">Raised</span><div>{fmtWhen(item.created_at)}</div></div>
        </div>

        {(item.viewed_by || []).length > 0 && (
          <div className="flex items-start gap-2 border-t border-summit-hairline pt-2.5 text-[11px] text-gray-600" data-testid="capital-viewed-by">
            <Eye className="w-3.5 h-3.5 mt-0.5 shrink-0 text-gray-400" />
            <div>
              <span className="font-mono text-[9px] uppercase tracking-widest text-gray-400 block mb-0.5">Viewed by</span>
              <div className="flex flex-wrap gap-x-3 gap-y-0.5">
                {item.viewed_by.map((v) => (
                  <span key={v.user_id} data-testid={`capital-viewed-stamp-${v.user_id}`}>
                    {v.name} <span className="font-mono text-[10px] text-gray-400">{fmtWhen(v.at)}</span>
                  </span>
                ))}
              </div>
            </div>
          </div>
        )}

        {item.description && (
          <div className="text-sm bg-summit-canvas/70 border border-summit-hairline rounded-md p-3 whitespace-pre-wrap" data-testid="capital-detail-description">{item.description}</div>
        )}

        {(item.approvals || []).length > 0 && (
          <div className="rounded-md p-3 text-sm border bg-emerald-50/60 border-emerald-200" data-testid="capital-approvals-list">
            <div className="font-mono text-[10px] uppercase tracking-widest text-emerald-800 mb-1.5">Approved by</div>
            <div className="space-y-1">
              {item.approvals.map((a) => (
                <div key={`${a.user_id}-${a.level}`} className="flex items-center gap-1.5 text-emerald-900" data-testid={`capital-approval-${a.user_id}`}>
                  <CheckCircle2 className="w-3.5 h-3.5 shrink-0" />
                  <span className="font-medium">{a.name}</span>
                  <span className="font-mono text-[10px] uppercase bg-emerald-100 px-1.5 rounded-full">L{a.level}</span>
                  {a.comment && <span className="text-xs italic truncate">&ldquo;{a.comment}&rdquo;</span>}
                  <span className="font-mono text-[10px] text-emerald-700/70 ml-auto shrink-0">{fmtWhen(a.at)}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {isAnyApprover && (
          <div className="flex items-center gap-2 text-xs border border-summit-hairline rounded-md px-3 py-2 bg-white" data-testid="capital-visibility-box">
            {(item.visibility || "private") === "members" ? <Users className="w-3.5 h-3.5 text-gray-500 shrink-0" /> : <Lock className="w-3.5 h-3.5 text-gray-500 shrink-0" />}
            <span className="font-mono text-[10px] uppercase tracking-widest text-gray-500 shrink-0">Visibility</span>
            <select
              className="ml-auto text-xs bg-transparent border border-summit-hairline rounded-md px-2 py-1 cursor-pointer"
              value={item.visibility || "private"}
              onChange={(e) => setVisibility(e.target.value)}
              data-testid="capital-visibility-select"
            >
              <option value="private">Requestor & approvers only</option>
              <option value="members">All members with access</option>
            </select>
          </div>
        )}

        {item.status !== "pending" && (item.status !== "approved" || (item.approvals || []).length === 0) && (
          <div className={`rounded-md p-3 text-sm border ${item.status === "approved" ? "bg-emerald-50 border-emerald-200 text-emerald-900" : "bg-rose-50 border-rose-200 text-rose-900"}`} data-testid="capital-decision-summary">
            <div className="flex items-center gap-1.5 font-medium">
              {item.status === "approved" ? <CheckCircle2 className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
              {item.status === "approved" ? "Approved" : "Denied"} by {item.decided_by_name} · {fmtWhen(item.decided_at)}
            </div>
            {item.decision_comment && <div className="mt-1 text-xs italic">&ldquo;{item.decision_comment}&rdquo;</div>}
          </div>
        )}

        {item.status === "pending" && isApprover && (
          <div className="border border-summit-hairline rounded-md p-3 space-y-2 bg-white">
            <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500">Your decision (L{level})</div>
            <Textarea rows={2} placeholder="Decision comment (optional)…" value={decisionComment} onChange={(e) => setDecisionComment(e.target.value)} data-testid="capital-decision-comment" />
            {level < 3 && (
              <div>
                <Label className="text-xs uppercase tracking-widest text-gray-500">Forward for further approval (optional)</Label>
                <Select value={forwardTo || "none"} onValueChange={(v) => setForwardTo(v === "none" ? "" : v)}>
                  <SelectTrigger className="mt-1" data-testid="capital-forward-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none" data-testid="capital-forward-option-none">No — final approval</SelectItem>
                    {forwardOptions.map((a) => (
                      <SelectItem key={a.id} value={a.id} data-testid={`capital-forward-option-${a.id}`}>
                        {a.name}{a.job_title ? ` — ${a.job_title}` : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {forwardTo && (
                  <p className="text-[10px] text-gray-500 mt-1" data-testid="capital-forward-note">
                    Approving forwards this request to the selected member as the L{level + 1} approver.
                  </p>
                )}
              </div>
            )}
            <div className="flex gap-2">
              <Button className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white" disabled={busy} onClick={() => decide("approved")} data-testid="capital-approve-btn">
                <CheckCircle2 className="w-4 h-4 mr-1.5" /> {forwardTo ? "Approve & forward" : "Approve"}
              </Button>
              <Button variant="outline" className="flex-1 border-rose-300 text-rose-700 hover:bg-rose-50" disabled={busy} onClick={() => decide("denied")} data-testid="capital-deny-btn">
                <XCircle className="w-4 h-4 mr-1.5" /> Deny
              </Button>
            </div>
          </div>
        )}
        {item.status === "pending" && !isApprover && (
          <div className="flex items-center gap-1.5 text-xs text-gray-500" data-testid="capital-awaiting-note">
            <Clock3 className="w-3.5 h-3.5" /> Awaiting decision from {item.approver_name} (L{level})
          </div>
        )}

        <AttachmentsSection item={item} user={user} busy={busy} setBusy={setBusy} onUpdated={onUpdated} />

        <div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500 mb-2">
            Discussion ({(item.comments || []).length})
          </div>          <div className="space-y-2 max-h-56 overflow-y-auto pr-1" data-testid="capital-comments-list">
            {(item.comments || []).length === 0 && <p className="text-xs text-gray-400">No comments yet — start the discussion.</p>}
            {(item.comments || []).map((c) => (
              <div key={c.id} className={`rounded-md px-3 py-2 text-sm ${c.user_id === user.id ? "bg-summit-glacier/70" : "bg-summit-canvas/70 border border-summit-hairline"}`} data-testid={`capital-comment-${c.id}`}>
                <div className="flex items-baseline gap-2">
                  <span className="font-medium text-xs">{c.user_id === user.id ? "You" : c.user_name}</span>
                  <span className="font-mono text-[10px] text-gray-400">{fmtWhen(c.created_at)}</span>
                </div>
                <div className="mt-0.5 whitespace-pre-wrap">{c.text}</div>
              </div>
            ))}
          </div>
          <div className="flex gap-2 mt-2">
            <Input
              placeholder="Add a comment…"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") sendComment(); }}
              data-testid="capital-comment-input"
            />
            <Button size="icon" className="shrink-0 bg-summit-primary hover:bg-summit-primary-soft text-white" disabled={busy || !comment.trim()} onClick={sendComment} data-testid="capital-comment-send">
              <Send className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {item.archived && (
          <div className="flex items-center gap-1.5 text-xs text-gray-500" data-testid="capital-archived-note">
            <Archive className="w-3.5 h-3.5" /> Archived by {item.archived_by_name} · {fmtWhen(item.archived_at)}
          </div>
        )}
        {(canEdit || canArchive || canDelete) && (
          <div className="flex items-center gap-4">
            {canEdit && (
              <button className="inline-flex items-center gap-1.5 text-xs text-summit-primary hover:underline" onClick={() => onEdit(item)} data-testid={`capital-edit-${item.id}`}>
                <Pencil className="w-3.5 h-3.5" /> Edit
              </button>
            )}
            {canArchive && (
              <button className="inline-flex items-center gap-1.5 text-xs text-gray-600 hover:underline disabled:opacity-50" onClick={archive} disabled={busy} data-testid={`capital-archive-${item.id}`}>
                <Archive className="w-3.5 h-3.5" /> Archive
              </button>
            )}
            {canDelete && (
              <button className="inline-flex items-center gap-1.5 text-xs text-rose-600 hover:underline" onClick={remove} data-testid={`capital-delete-${item.id}`}>
                <Trash2 className="w-3.5 h-3.5" /> Delete request
              </button>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function Capital() {
  const { user } = useAuth();
  const [items, setItems] = useState(null);
  const [approvers, setApprovers] = useState([]);
  const [tab, setTab] = useState("pending");
  const [fy, setFy] = useState(String(CURRENT_FY));
  const [page, setPage] = useState(1);
  const [openForm, setOpenForm] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [detailId, setDetailId] = useState(null);

  const hasAccess = user && (user.capital_access || user.role === "admin");

  const load = async () => {
    try {
      const [a, b] = await Promise.all([api.get("/capital"), api.get("/capital/approvers")]);
      setItems(a.data || []);
      setApprovers(b.data || []);
    } catch (e) {
      setItems([]);
      toast.error(e?.response?.data?.detail || "Failed to load capital items");
    }
  };
  useEffect(() => { if (hasAccess) load(); }, [hasAccess]); // eslint-disable-line react-hooks/exhaustive-deps

  const fyOptions = useMemo(() => {
    const years = new Set([CURRENT_FY]);
    (items || []).forEach((i) => years.add(fyOf(i.created_at)));
    return [...years].sort((a, b) => b - a);
  }, [items]);

  const fyItems = useMemo(() => {
    const all = items || [];
    return fy === "all" ? all : all.filter((i) => fyOf(i.created_at) === Number(fy));
  }, [items, fy]);

  const counts = useMemo(() => {
    const live = fyItems.filter((i) => !i.archived);
    return {
      all: live.length,
      pending: live.filter((i) => i.status === "pending").length,
      approved: live.filter((i) => i.status === "approved").length,
      denied: live.filter((i) => i.status === "denied").length,
      archived: fyItems.filter((i) => i.archived).length,
    };
  }, [fyItems]);

  const visible = useMemo(() => {
    if (tab === "archived") return fyItems.filter((i) => i.archived);
    const live = fyItems.filter((i) => !i.archived);
    return tab === "all" ? live : live.filter((i) => i.status === tab);
  }, [fyItems, tab]);

  const fyTotals = useMemo(() => {
    const t = { pending: {}, approved: {}, denied: {} };
    fyItems.forEach((i) => {
      if (i.estimated_cost === null || i.estimated_cost === undefined) return;
      const b = t[i.status];
      if (b) b[i.currency] = (b[i.currency] || 0) + Number(i.estimated_cost);
    });
    return t;
  }, [fyItems]);
  const fmtTotals = (b) => {
    const e = Object.entries(b);
    return e.length ? e.map(([c, v]) => money(v, c)).join(" + ") : "—";
  };

  const totalPages = Math.max(1, Math.ceil(visible.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pageRows = visible.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);
  const detailItem = (items || []).find((i) => i.id === detailId) || null;

  const exportXlsx = async () => {
    try {
      const resp = await fetch(`${API_BASE}/capital/export?fy=${fy}`, { headers: { Authorization: `Bearer ${getToken()}` } });
      if (!resp.ok) throw new Error();
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Capital-${fy === "all" ? "all-years" : `FY-${fy}`}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Export failed");
    }
  };

  const printReport = () => {
    printCapitalReport({
      fyText: fy === "all" ? "All years" : fyLabel(Number(fy)),
      totals: fyTotals,
      rows: fyItems,
    });
  };

  if (!user) return null;
  if (!hasAccess) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-16 text-center" data-testid="capital-no-access">
        <Landmark className="w-8 h-8 mx-auto text-gray-300" />
        <h1 className="font-display text-2xl font-semibold mt-4">Capital Overview</h1>
        <p className="text-sm text-gray-500 mt-2">You don&apos;t have access to this page. Ask an admin to grant it in Admin Settings.</p>
      </div>
    );
  }

  return (
    <div className="w-full mx-auto px-4 sm:px-[15%] py-10">
      <div className="rounded-xl overflow-hidden border border-summit-hairline shadow-sm" data-testid="capital-masthead">
        <div className="relative bg-[#0c1a2e] px-6 sm:px-8 pt-7 pb-6">
          <div className="absolute inset-x-0 top-0 h-[3px] bg-gradient-to-r from-[#8a6d2f] via-[#e2c67d] to-[#8a6d2f]" />
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight text-white" data-testid="capital-heading">Capital Overview</h1>
              <p className="text-sm text-slate-400 mt-1.5 max-w-xl">Raise, discuss and approve large-value expenditures. Requests stay between the requestor and their approval chain unless an approver opens them up to all members.</p>
            </div>
            <div className="flex gap-2 pt-1 flex-wrap">
              <Button variant="outline" className="border-white/25 bg-transparent text-white hover:bg-white/10 hover:text-white" onClick={exportXlsx} data-testid="capital-export-btn">
                <Download className="w-4 h-4 mr-1.5" /> Export
              </Button>
              <Button variant="outline" className="border-white/25 bg-transparent text-white hover:bg-white/10 hover:text-white" onClick={printReport} data-testid="capital-print-btn">
                <Printer className="w-4 h-4 mr-1.5" /> Print report
              </Button>
              <Button className="bg-[#c2a14d] hover:bg-[#d0b060] text-[#0c1a2e] font-semibold" onClick={() => { setEditItem(null); setOpenForm(true); }} data-testid="capital-new-btn">
                <Plus className="w-4 h-4 mr-1.5" /> Raise request
              </Button>
            </div>
          </div>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 divide-x divide-summit-hairline bg-white" data-testid="capital-fy-summary">
          <div className="px-5 py-3.5">
            <div className="font-mono text-[9px] uppercase tracking-[0.25em] text-gray-400">Fiscal year</div>
            <select
              className="mt-1 text-sm font-display font-medium bg-transparent border-0 p-0 pr-4 focus:ring-0 focus:outline-none cursor-pointer"
              value={fy}
              onChange={(e) => { setFy(e.target.value); setPage(1); }}
              data-testid="capital-fy-select"
            >
              {fyOptions.map((y) => <option key={y} value={String(y)}>{fyLabel(y)}</option>)}
              <option value="all">All years</option>
            </select>
          </div>
          {[["pending", "Pending", "text-amber-700"], ["approved", "Approved", "text-emerald-700"], ["denied", "Denied", "text-rose-700"]].map(([k, l, cls]) => (
            <div key={k} className="px-5 py-3.5">
              <div className={`font-mono text-[9px] uppercase tracking-[0.25em] ${cls}`}>{l}</div>
              <div className="mt-1 text-sm font-display font-medium" data-testid={`capital-fy-${k}`}>{fmtTotals(fyTotals[k])}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-4 flex items-center gap-2 flex-wrap">
        {[["all", "All"], ["pending", "Pending"], ["approved", "Approved"], ["denied", "Denied"], ["archived", "Archived"]].map(([key, label]) => (
          <button
            key={key}
            onClick={() => { setTab(key); setPage(1); }}
            className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${tab === key ? "bg-summit-primary text-white" : "bg-white border border-summit-hairline text-gray-600 hover:bg-summit-glacier"}`}
            data-testid={`capital-tab-${key}`}
          >
            {label} · {counts[key]}
          </button>
        ))}
      </div>

      <div className="mt-4 space-y-3">
        {items === null ? (
          <div className="text-center text-gray-400 text-sm py-12">Loading…</div>
        ) : visible.length === 0 ? (
          <div className="text-center text-gray-400 text-sm py-12 border border-dashed border-summit-hairline rounded-lg" data-testid="capital-empty">
            {tab === "all" ? "No capital requests in this fiscal year." : `No ${tab} requests.`}
          </div>
        ) : (
          pageRows.map((i) => (
            <button
              key={i.id}
              className="w-full text-left border border-summit-hairline rounded-lg bg-white px-4 py-3 hover:bg-summit-glacier/30 transition-colors"
              onClick={() => setDetailId(i.id)}
              data-testid={`capital-card-${i.id}`}
            >
              <div className="flex items-center gap-2 flex-wrap">
                <span className={`px-2 py-0.5 rounded-full font-mono text-[10px] uppercase tracking-widest ${PRIORITY_CLS[i.priority] || "bg-gray-100 text-gray-600"}`}>{i.priority}</span>
                <span className="font-medium text-sm truncate">{i.title}</span>
                {i.has_unread && (
                  <span className="px-1.5 py-0.5 rounded-full bg-rose-600 text-white font-mono text-[9px] uppercase tracking-widest" data-testid={`capital-new-badge-${i.id}`}>New</span>
                )}
                <span className="font-mono text-[10px] uppercase tracking-widest text-gray-400">{i.category}{i.fleet ? ` · ${i.fleet}` : ""}</span>
                <span className="ml-auto font-mono text-xs">{money(i.estimated_cost, i.currency)}</span>
                {i.archived && <span className="px-2 py-0.5 rounded-full font-mono text-[10px] uppercase tracking-widest bg-gray-200 text-gray-600">archived</span>}
                {(i.visibility || "private") === "private" && <Lock className="w-3 h-3 text-gray-400" data-testid={`capital-private-${i.id}`} />}
                <CapitalStatusBadge status={i.status} />
              </div>
              <div className="flex items-center gap-3 mt-1.5 text-[11px] text-gray-500">
                <span>{i.created_by_name} → {i.approver_name}{(i.approval_level || 1) > 1 ? ` (L${i.approval_level})` : ""}</span>
                <ChainStrip item={i} />
                <span className="font-mono text-[10px] text-gray-400">{fmtWhen(i.created_at)}</span>
                <span className="inline-flex items-center gap-1 ml-auto text-gray-400">
                  <span className="inline-flex items-center gap-0.5 mr-2" data-testid={`capital-viewed-count-${i.id}`}>
                    <Eye className="w-3 h-3" /> {(i.viewed_by || []).length}
                  </span>
                  {(i.attachments || []).length > 0 && (
                    <span className="inline-flex items-center gap-0.5 mr-2"><Paperclip className="w-3 h-3" /> {(i.attachments || []).length}</span>
                  )}
                  <MessageSquare className="w-3 h-3" /> {(i.comments || []).length}
                </span>
              </div>
            </button>
          ))
        )}
      </div>

      {visible.length > PAGE_SIZE && (
        <div className="mt-4 flex items-center justify-between border-t border-summit-hairline pt-3">
          <span className="font-mono text-[10px] uppercase tracking-widest text-gray-500" data-testid="capital-page-label">
            Page {safePage} of {totalPages} · {visible.length} requests
          </span>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" disabled={safePage <= 1} onClick={() => setPage(safePage - 1)} data-testid="capital-prev">Previous</Button>
            <Button size="sm" variant="outline" disabled={safePage >= totalPages} onClick={() => setPage(safePage + 1)} data-testid="capital-next">Next</Button>
          </div>
        </div>
      )}

      <RaiseDialog
        open={openForm}
        onOpenChange={(o) => { setOpenForm(o); if (!o) setEditItem(null); }}
        approvers={approvers}
        initial={editItem}
        onSaved={(it) => setItems((xs) => {
          const arr = xs || [];
          const exists = arr.some((x) => x.id === it.id);
          return (exists ? arr.map((x) => (x.id === it.id ? it : x)) : [it, ...arr]).sort(byActivity);
        })}
      />
      {detailItem && (
        <DetailDialog
          item={detailItem}
          approvers={approvers}
          onOpenChange={(o) => { if (!o) setDetailId(null); }}
          user={user}
          onUpdated={(it) => setItems((xs) => (xs || []).map((x) => (x.id === it.id ? it : x)).sort(byActivity))}
          onDeleted={(id) => setItems((xs) => (xs || []).filter((x) => x.id !== id))}
          onEdit={(it) => { setEditItem(it); setOpenForm(true); }}
        />
      )}
    </div>
  );
}
